import {
  Button,
  Checkbox,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Typography,
} from "antd";
import {
  EPB_ATTRIBUTE_ADD_ENDPOINT,
  EPB_ATTRIBUTE_DELETE_ENDPOINT,
  EPB_ATTRIBUTE_EDIT_ENDPOINT,
  EPB_ATTRIBUTE_LIST_ENDPOINT,
} from "apiServices/API_ENDPOINTS";
import { deleteData, getData, postData } from "apiServices/common";
import AppContent from "common/AppContent";
import ListTable from "common/ListTable";
import AppPageHeader from "common/PageHeader";
import ExtraContent from "components/Configuration/CommonComponents/ExtraContent";
import FilterComponent from "components/Configuration/CommonComponents/FilterComponent";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { NO_PERMISSION_REQUIRED } from "routes/permissions";
import { hasPermission } from "utils/functions";

export default function EPBAttribute() {
  // Router
  const location = useLocation();
  const routerPath = location ? location.pathname : null;

  // States
  const [showFilter, setShowFilter] = useState(
    localStorage.getItem("filter_status") && routerPath
      ? JSON.parse(localStorage.getItem("filter_status"))[routerPath]
      : false,
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [metaData, setMetaData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  // Antd
  const [epbAttributeForm] = Form.useForm();
  const { Title } = Typography;

  // Redux
  const filterData = useSelector(({ filter }) => filter.filterData);

  // Filter open/close status update in localStorage
  useEffect(() => {
    const filterStatus = localStorage.getItem("filter_status");
    const parsedFilterStatus = filterStatus ? JSON.parse(filterStatus) : {};

    if (showFilter) {
      localStorage.setItem(
        "filter_status",
        JSON.stringify({
          ...parsedFilterStatus,
          [routerPath]: true,
        }),
      );
    } else {
      localStorage.setItem(
        "filter_status",
        JSON.stringify({
          ...parsedFilterStatus,
          [routerPath]: false,
        }),
      );
    }
  }, [showFilter, routerPath]);

  // Define field type options
  const fieldTypeOptions = [
    { value: "text", label: "Text" },
    { value: "textarea", label: "Textarea" },
  ];

  // No need to load dropdown options for this component

  const getAllEPBAttributes = useCallback(
    async (filterValues) => {
      const { name, field_type } = filterValues || {};

      setLoading(true);
      setFetchError(null);

      const query = `${EPB_ATTRIBUTE_LIST_ENDPOINT}?page=${currentPage}`;
      const bodyData = {
        ...(name && { name }),
        ...(field_type && { field_type }),
      };

      try {
        const response = await getData(query, false, bodyData);
        if (response?.status === 200) {
          const data = response.data?.data?.data || [];
          // Add unique key if id is missing
          const dataWithKeys = data.map((item, index) => ({
            ...item,
            key: item.id || `epb-attribute-${index}`,
          }));

          setTableData(dataWithKeys);
          setMetaData({
            current: response.data?.data?.current_page || 1,
            defaultCurrent: response?.data?.data?.from || 1,
            defaultPageSize: response?.data?.data?.per_page || 10,
            total: response.data?.data?.total || 0,
          });
        } else {
          setFetchError("Could not load this list.");
        }
      } catch (error) {
        console.error("EPB Attribute fetch error:", error);
        setFetchError("Could not load this list.");
      } finally {
        setLoading(false);
      }
    },
    [currentPage],
  );

  // Effects
  useEffect(() => {
    getAllEPBAttributes(filterData);
  }, [refresh, filterData, getAllEPBAttributes]);

  const onModalOpen = () => {
    setIsModalOpen(true);
  };

  const onCloseModal = useCallback(() => {
    epbAttributeForm.resetFields();
    setIsModalOpen(false);
    setIsEdit(false);
  }, [epbAttributeForm]);

  const refetch = useCallback(() => {
    setRefresh((prev) => prev + 1);
  }, []);

  const deleteSelectedEPBAttribute = useCallback(
    async (id) => {
      setLoading(true);
      try {
        // Confirm before deleting
        Modal.confirm({
          title: "Confirm Deletion",
          content: "Are you sure you want to delete this EPB Attribute?",
          okText: "Yes, Delete",
          okType: "danger",
          cancelText: "Cancel",
          onOk: async () => {
            try {
              const response = await deleteData(
                `${EPB_ATTRIBUTE_DELETE_ENDPOINT}/${id}`,
              );

              if (
                response &&
                (response?.code === 200 || response?.status === 200)
              ) {
                refetch();
              }
            } catch (error) {
              console.error("Delete EPB attribute error:", error);
            } finally {
              setLoading(false);
            }
          },
          onCancel: () => {
            setLoading(false);
          },
        });
      } catch (error) {
        console.error("Delete EPB attribute confirmation error:", error);
        setLoading(false);
      }
    },
    [refetch],
  );

  const handleEPBAttributeSubmit = useCallback(
    async (formValues) => {
      setLoading(true);
      const isEditMode = !!formValues.id;

      // Request body based on API requirements
      const bodyData = {
        name: formValues?.name,
        is_required: formValues?.is_required ? 1 : 0,
        field_type: formValues?.field_type,
        ...(isEditMode && { id: formValues.id }), // Include ID for update operations
      };

      try {
        let response;

        if (isEditMode) {
          // Edit operation - POST request (API doesn't support PUT)
          response = await postData(
            `${EPB_ATTRIBUTE_EDIT_ENDPOINT}/${formValues.id}`,
            bodyData,
          );
        } else {
          // Create operation - POST request
          response = await postData(EPB_ATTRIBUTE_ADD_ENDPOINT, bodyData);
        }

        if (response && (response?.code === 200 || response?.status === 200)) {
          onCloseModal();
          refetch();
        }
      } catch (error) {
        console.error(
          `${isEditMode ? "Edit" : "Create"} EPB attribute error:`,
          error,
        );
      } finally {
        setLoading(false);
      }
    },
    [onCloseModal, refetch],
  );

  const openModalForEdit = useCallback(
    (record) => {
      setIsEdit(true);
      epbAttributeForm.setFieldsValue({
        id: record.id,
        name: record.name,
        is_required: record.is_required,
        field_type: record.field_type,
      });
      setIsModalOpen(true);
    },
    [epbAttributeForm],
  );

  const changePage = (newPage) => {
    setCurrentPage(newPage);
  };

  const tableColumns = useMemo(
    () => [
      {
        title: "Attribute Name",
        dataIndex: "name",
        width: "25%",
        key: "name",
        ellipsis: true,
      },
      {
        title: "Field Type",
        dataIndex: "field_type",
        width: "20%",
        key: "field_type",
        ellipsis: true,
      },
      {
        title: "Required",
        dataIndex: "is_required",
        width: "15%",
        key: "is_required",
        ellipsis: true,
        render: (value) => (value === "1" ? "Yes" : "No"),
      },

      {
        title: "Action",
        key: "action",
        width: "10%",
        render: (_, record) => (
          <div
            className="action-buttons"
            style={{ display: "flex", gap: "8px" }}
          >
            {hasPermission([NO_PERMISSION_REQUIRED]) && (
              <Button
                type="primary"
                size="small"
                onClick={() => openModalForEdit(record)}
                className="edit-button"
                style={{ backgroundColor: "#1890ff" }}
              >
                Edit
              </Button>
            )}
            {hasPermission([NO_PERMISSION_REQUIRED]) && (
              <Button
                type="primary"
                danger
                size="small"
                onClick={() => deleteSelectedEPBAttribute(record.id)}
                className="delete-button"
              >
                Delete
              </Button>
            )}
          </div>
        ),
      },
    ],
    [openModalForEdit, deleteSelectedEPBAttribute],
  );

  const memoizedCustomFilter = useMemo(
    () => (
      <FilterComponent
        tableData={tableData}
        filterAPI={getAllEPBAttributes}
        showFilter={showFilter}
        setShowFilter={setShowFilter}
      />
    ),
    [showFilter, tableData, getAllEPBAttributes],
  );

  return (
    <>
      <AppContent
        header={
          <AppPageHeader
            extra={
              <ExtraContent
                refresh={refresh}
                setRefresh={setRefresh}
                handleAddItemOpen={onModalOpen}
                disabled={!hasPermission([NO_PERMISSION_REQUIRED])}
                addButtonText={
                  hasPermission([NO_PERMISSION_REQUIRED])
                    ? "Add New"
                    : "No Permission"
                }
                showFilter={showFilter}
                setShowFilter={setShowFilter}
              />
            }
            title={<Title level={5}>EPB Attribute Configuration</Title>}
          />
        }
        content={
          <>
            {memoizedCustomFilter}

            <div className="table" style={{ marginTop: 10 }}>
              <ListTable
                loading={loading}
                error={fetchError}
                onRetry={() => getAllEPBAttributes(filterData)}
                dataSource={tableData}
                columns={tableColumns}
                rowKey="id"
                pagination={{
                  showSizeChanger: false,
                  defaultPageSize: metaData?.defaultPageSize || 10,
                  current: metaData?.current || 1,
                  onChange: changePage,
                  defaultCurrent: metaData?.defaultCurrent || 1,
                  total: metaData?.total || 0,
                  showTotal: (total) => `Total ${total} items`,
                }}
                bordered
                size="middle"
                scroll={{
                  x: "max-content",
                  y: tableData.length > 10 ? 500 : undefined,
                }}
                rowClassName={(record, index) =>
                  index % 2 === 0 ? "table-row-light" : "table-row-dark"
                }
                locale={{
                  emptyText: loading
                    ? "Loading data..."
                    : "No EPB attributes found",
                }}
                style={{ borderRadius: "8px", overflow: "hidden" }}
              />
            </div>
          </>
        }
      />
      <Modal
        open={isModalOpen}
        title={
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {isEdit ? (
              <span style={{ color: "#1890ff" }}>Edit EPB Attribute</span>
            ) : (
              <span style={{ color: "#52c41a" }}>Create New EPB Attribute</span>
            )}
          </div>
        }
        onCancel={onCloseModal}
        maskStyle={{ backdropFilter: "blur(4px)" }}
        footer={null}
        destroyOnClose
        width={520}
        centered
        bodyStyle={{ padding: "24px" }}
      >
        <Form
          form={epbAttributeForm}
          layout="vertical"
          onFinish={handleEPBAttributeSubmit}
          initialValues={{}}
          requiredMark="optional"
        >
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item name="id" hidden />
              <Form.Item
                name="name"
                label="Attribute Name"
                rules={[
                  {
                    required: true,
                    message: "Attribute Name is required",
                  },
                  {
                    max: 100,
                    message: "Attribute Name cannot exceed 100 characters",
                  },
                ]}
              >
                <Input
                  placeholder="Enter attribute name"
                  autoFocus
                  autoComplete="off"
                  style={{ borderRadius: "4px" }}
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="field_type"
                label="Field Type"
                rules={[
                  {
                    required: true,
                    message: "Field Type is required",
                  },
                ]}
              >
                <Select
                  placeholder="Select field type"
                  options={fieldTypeOptions}
                  showSearch
                  filterOption={(input, option) =>
                    option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                  style={{ width: "100%", borderRadius: "4px" }}
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="is_required"
                valuePropName="checked"
                initialValue={false}
              >
                <Checkbox style={{ lineHeight: "32px" }}>Is Required</Checkbox>
              </Form.Item>
            </Col>
          </Row>
          <Row
            gutter={16}
            className="form-actions"
            style={{ marginTop: "12px" }}
          >
            <Col span={12}>
              <Button
                type="default"
                danger
                style={{ width: "100%", borderRadius: "4px" }}
                onClick={onCloseModal}
              >
                Cancel
              </Button>
            </Col>
            <Col span={12}>
              <Button
                type="primary"
                htmlType="submit"
                style={{ width: "100%", borderRadius: "4px" }}
                loading={loading}
              >
                {isEdit ? "Update" : "Save"}
              </Button>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
}
